import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Button,
  Image,
  Space,
  Typography,
  Spin,
  Tag,
  message,
} from "antd";
import {
  LeftOutlined,
  RightOutlined,
  CheckCircleOutlined,
  StopOutlined,
  QuestionCircleOutlined,
  PicCenterOutlined,
} from "@ant-design/icons";
import styles from "./styles.module.css";
import { GetRowerPhotos, UpdateRowerPhotographStatus } from "../../APIManager";

const { Title, Text } = Typography;

const StatusTag = ({ status }) => {
  let color = "default";
  let icon = <QuestionCircleOutlined />;
  if (status === "Verified") {
    color = "success";
    icon = <CheckCircleOutlined />;
  } else if (status === "Noncompliant") {
    color = "error";
    icon = <StopOutlined />;
  } else if (status === "Unverified") {
    color = "warning";
    icon = <QuestionCircleOutlined />;
  }
  return (
    <Tag color={color} icon={icon}>
      {status}
    </Tag>
  );
};

const StatusButton = ({
  status,
  currentStatus,
  onClick,
  icon,
  text,
  color,
}) => (
  <Button
    type={currentStatus === status ? "primary" : "default"}
    icon={icon}
    onClick={() => onClick(status)}
    // Apply custom class for shadow effect when active
    className={`${styles.widthFull} ${
      currentStatus === status ? styles.statusButtonActive : ""
    }`}
    // Use inline style to control Ant Design's color palette based on status
    style={{
      transition: "all 0.2s",
      backgroundColor: currentStatus === status ? color : "white",
      borderColor: currentStatus === status ? color : "#d9d9d9",
      color: currentStatus === status ? "white" : "rgba(0, 0, 0, 0.85)",
    }}
  >
    {text}
  </Button>
);

// --- Main Application Component ---

const App = () => {
  // Stores the master list of all students (unfiltered)
  const [students, setStudents] = useState([]);
  // Index within the currently filtered list
  const [currentIndex, setCurrentIndex] = useState(0);
  // Filter state: 'all', 'unverified', 'noncompliant'
  const [filter, setFilter] = useState("unverified");
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchStudents = async () => {
    GetRowerPhotos().then((response) => {
      setStudents(response.data);
    });
  };

  // 1. Fetch Data on Mount
  useEffect(() => {
    fetchStudents()
      .then((data) => {
        setLoading(false);
      })
      .catch((err) => {
        message.error("Failed to load student data.");
        setLoading(false);
        console.error(err);
      });
  }, []);

  // 2. Filter Students
  const filteredStudents = useMemo(() => {
    const statusMap = {
      unverified: "Unverified",
      noncompliant: "Noncompliant",
      verified: "Verified",
    };

    if (filter === "all") {
      return students;
    }

    const requiredStatus = statusMap[filter];
    return students.filter((s) => s.PhotographStatus === requiredStatus);
  }, [students, filter]);

  // 3. Current Student and Navigation Info (based on filtered list)
  const student = useMemo(
    () => filteredStudents[currentIndex],
    [filteredStudents, currentIndex]
  );
  const totalStudents = filteredStudents.length;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex >= totalStudents - 1; // Check against length to handle 0-length case

  // Find the original index of the current student in the master list
  const currentStudentInAllListIndex = useMemo(() => {
    if (!student) return -1; // ONLY use the unique identifier _id, as specified by the user's latest input
    return students.findIndex((s) => s._id === student._id);
  }, [students, student]);

  // 4. Reset Index when Filter Changes
  useEffect(() => {
    // Reset index to 0 when filter changes
    setCurrentIndex(0);
  }, [filter, loading, filteredStudents.length]);

  // 5. Navigation Logic
  const nextStudent = useCallback(() => {
    if (!isLast) {
      setCurrentIndex((prev) => prev + 1);
    } else if (totalStudents > 0) {
      message.info("You've reached the end of the current filter list!");
    }
  }, [isLast, totalStudents]);

  const prevStudent = useCallback(() => {
    if (!isFirst) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [isFirst]);

  // 6. Status Update Logic
  const updateStatus = useCallback(
    async (newStatus) => {
      if (isUpdating || !student) return;
      const originalIndex = currentStudentInAllListIndex;

      if (originalIndex === -1) {
        message.error("Could not locate student for update.");
        return;
      }

      const originalStudent = students[originalIndex];
      const oldStatus = originalStudent.PhotographStatus; // 1. Optimistically update local state
      setStudents((prevStudents) => {
        const newStudents = [...prevStudents];
        newStudents[originalIndex] = {
          ...newStudents[originalIndex],
          PhotographStatus: newStatus,
        };
        return newStudents;
      });
      setIsUpdating(true);

      try {
        // 2. Call the server API using student._id as RowerID
        if (!originalStudent._id) {
          throw new Error("Missing Rower ID (_id) for API call.");
        }
        await UpdateRowerPhotographStatus({
          RowerID: originalStudent._id, // *** KEY CHANGE: Using _id here ***
          PhotographStatus: newStatus,
        }); // 3. Success Feedback

        /*  message.success(
          `Status updated to "${newStatus}" for ${originalStudent.Name}.`
        ); // 4. Handle index change for filtered views on SUCCESS // (Omitted for brevity, but remains the same as previous response) */

        if (newStatus !== filter && filter !== "all") {
          const listLengthAfterRemoval = filteredStudents.length - 1;
          if (
            currentIndex >= listLengthAfterRemoval &&
            listLengthAfterRemoval > 0
          ) {
            setCurrentIndex(listLengthAfterRemoval - 1);
          } else if (listLengthAfterRemoval === 0) {
            setCurrentIndex(0);
          }
        }
      } catch (error) {
        // 5. Rollback on Error
        console.error("Update failed:", error);
        message.error(
          `Failed to update status for ${originalStudent.Name}. Rolling back.`
        ); // Rollback the local state change
        setStudents((prevStudents) => {
          const revertedStudents = [...prevStudents];
          revertedStudents[originalIndex] = {
            ...revertedStudents[originalIndex],
            PhotographStatus: oldStatus,
          };
          return revertedStudents;
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [
      isUpdating,
      student,
      students,
      currentStudentInAllListIndex,
      filter,
      currentIndex,
      filteredStudents.length,
    ]
  );

  // 7. Keyboard Navigation (Unchanged)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (loading || totalStudents === 0) return; // Disable navigation if list is empty

      switch (event.key) {
        case "ArrowRight":
          nextStudent();
          break;
        case "ArrowLeft":
          prevStudent();
          break;
        case "v": // 'v' for Verified
        case "V":
          updateStatus("Verified");
          break;
        case "u": // 'u' for Unverified
        case "U":
          updateStatus("Unverified");
          break;
        case "n": // 'n' for Noncompliant
        case "N":
          updateStatus("Noncompliant");
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [nextStudent, prevStudent, updateStatus, loading, totalStudents]);

  // 8. Render Logic
  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin tip="Loading student data..." size="large" />
      </div>
    );
  }

  // Main Container always renders after loading
  return (
    <div className={styles.moderatorContainer}>
      <Card
        title={
          <div className={styles.cardTitle}>
            <Title level={4} className={styles.titleText}>
              Photo Verification Queue
            </Title>
            <Text strong className="text-lg">
              {totalStudents > 0
                ? `${currentIndex + 1} / ${totalStudents}`
                : "0 / 0"}
              <span className={styles.statusInfo}>
                (
                {filter === "all"
                  ? "All"
                  : filter.charAt(0).toUpperCase() + filter.slice(1)}
                )
              </span>
            </Text>
          </div>
        }
        className={styles.moderatorCard}
      >
        {/* Filter Controls (ALWAYS VISIBLE) */}
        <div className={styles.filterControls}>
          <Text strong className={styles.filterLabel}>
            Filter View:
          </Text>
          <Button.Group>
            <Button
              type={filter === "all" ? "primary" : "default"}
              icon={<PicCenterOutlined />}
              onClick={() => setFilter("all")}
            >
              Show All
            </Button>
            <Button
              type={filter === "verified" ? "primary" : "default"}
              icon={<CheckCircleOutlined />}
              onClick={() => setFilter("verified")}
              // Use custom style for yellow button on Ant Design primary type
              style={
                filter === "verified"
                  ? {
                      backgroundColor: "#52c41a",
                      borderColor: "#52c41a",
                      color: "white",
                    }
                  : {}
              }
            >
              Verified
            </Button>
            <Button
              type={filter === "unverified" ? "primary" : "default"}
              icon={<QuestionCircleOutlined />}
              onClick={() => setFilter("unverified")}
              // Use custom style for yellow button on Ant Design primary type
              style={
                filter === "unverified"
                  ? {
                      backgroundColor: "#faad14",
                      borderColor: "#faad14",
                      color: "white",
                    }
                  : {}
              }
            >
              Unverified
            </Button>
            <Button
              type={filter === "noncompliant" ? "primary" : "default"}
              icon={<StopOutlined />}
              onClick={() => setFilter("noncompliant")}
              danger={filter === "noncompliant"}
            >
              Noncompliant
            </Button>
          </Button.Group>
        </div>

        {/* Conditional Content based on filtered list size */}
        {totalStudents === 0 || !student ? (
          <div className={styles.emptyState}>
            <StopOutlined style={{ fontSize: "48px", color: "#ccc" }} />
            <Title level={3} className="mt-4 text-gray-700">
              {filter === "all"
                ? "The Verification Queue is Empty!"
                : `No rowers found matching filter: ${filter}`}
            </Title>
            <Text className="text-gray-500">
              {filter === "all"
                ? "All photos have been processed."
                : "Please select a different filter to continue verifying."}
            </Text>
          </div>
        ) : (
          // Existing Photo/Controls Grid
          <div className={styles.contentGrid}>
            {/* Image and Details Section */}
            <div className={styles.detailsSection}>
              <Title level={5} className="mt-0">
                Student Information
              </Title>
              <div className={styles.infoBox}>
                <Text className={styles.infoItem}>
                  <span className={styles.fontWeightSemibold}>Name:</span>{" "}
                  {student.Name}
                </Text>
                <Text className={styles.infoItem}>
                  <span className={styles.fontWeightSemibold}>Age Group:</span>{" "}
                  {student.AgeGroup}
                </Text>
                <Text className={styles.infoItem}>
                  <span className={styles.fontWeightSemibold}>School:</span>{" "}
                  {student.Team.schoolname}
                </Text>
                <Text className={styles.infoItem}>
                  <span className={styles.fontWeightSemibold}>
                    Current Status:
                  </span>{" "}
                  <StatusTag status={student.PhotographStatus} />
                </Text>
              </div>

              <div className={styles.photoWrapper}>
                <Image
                  // 1. Force React to remount on change
                  key={student._id}
                  // 2. Fill the wrapper completely
                  width="100%"
                  height="100%"
                  // 3. Ensure the image covers the area (since ratio matches, 'cover' is safe and looks best)
                  style={{ objectFit: "contain" }}
                  src={`https://api.isr.kbcrowing.org/DownloadFile/${encodeURIComponent(
                    student.Photograph
                  )}`}
                  // 4. Clean loader centered by the wrapper's flexbox
                  placeholder={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <Spin size="large" />
                    </div>
                  }
                  alt={`Photograph of ${student.Name}`}
                  fallback="https://placehold.co/311x400/cccccc/000000?text=Image+Load+Error"
                />
              </div>
            </div>

            {/* Controls Section */}
            <div className={styles.controlsSection}>
              <Space
                direction="vertical"
                size="middle"
                className={styles.widthFull}
              >
                <Title level={5} className="mt-0">
                  Verification Action
                </Title>

                {/* Status Buttons */}
                <StatusButton
                  status="Verified"
                  currentStatus={student.PhotographStatus}
                  onClick={updateStatus}
                  icon={<CheckCircleOutlined />}
                  text="Verified (V)"
                  color="#52c41a"
                />
                <StatusButton
                  status="Unverified"
                  currentStatus={student.PhotographStatus}
                  onClick={updateStatus}
                  icon={<QuestionCircleOutlined />}
                  text="Unverified (U)"
                  color="#faad14"
                />
                <StatusButton
                  status="Noncompliant"
                  currentStatus={student.PhotographStatus}
                  onClick={updateStatus}
                  icon={<StopOutlined />}
                  text="Noncompliant (N)"
                  color="#ff4d4f"
                />
              </Space>

              {/* Navigation Buttons */}
              <Space size="small" className={styles.widthFull}>
                <Button
                  type="default"
                  icon={<LeftOutlined />}
                  onClick={prevStudent}
                  disabled={isFirst}
                  className={styles.navButtonLeft}
                >
                  Prev (←)
                </Button>
                <Button
                  type="default"
                  icon={<RightOutlined />}
                  onClick={nextStudent}
                  disabled={isLast}
                  className={styles.navButtonRight}
                >
                  Next (→)
                </Button>
              </Space>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default App;
